import { Dispatch, FC } from "react"

import { ReactComponent as Anonymous } from "@common_assets/svg/anonymous.svg"
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material"
import { useMutationPostCreateJobs } from "api/reactQuery/mutations/jobs"
import { useQueryClassesById } from "api/reactQuery/queries/classes"
import Dialog from "components/common/dialog/dialog"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useDialog } from "hooks/dialog"
import { useCustomSnackbar } from "hooks/snackbar"
import { useSuggestedJobs } from "hooks/suggestedJobs"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useParams } from "react-router"
import { getErrorMessageFromTab } from "utils/api"

import AddJob from "../addJob"
import AddSuggestedJobs from "../addSuggestedJobs"

interface IProps {
  setRichEditorResponsibilities: Dispatch<React.SetStateAction<string>>
  richEditorResponsibilities: string
  richEditorQualifications: string
  setRichEditorQualifications: Dispatch<React.SetStateAction<string>>
  onAddJob?: () => void
  onAddSuggestedJobs?: () => void
}

const NoJobs: FC<IProps> = ({
  richEditorResponsibilities,
  setRichEditorResponsibilities,
  setRichEditorQualifications,
  richEditorQualifications,
  onAddJob,
  onAddSuggestedJobs,
}) => {
  const params = useParams()
  const theme = useTheme()
  const isMobile = useIsBreakpointUp("tablet")
  const betweenTwoSides = useMediaQuery(theme.breakpoints.between(1024, 1224))
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const queryClient = useQueryClient()

  const {
    checkedSuggestedJobs,
    setCheckedSuggestedJobs,
    errorSuggested,
    isDialogOpenSuggested,
    handleOpenDialogSuggested,
    isDialogCloseSuggestedJobs,
    handleOpenDialogCloseSuggestedJobs,
    handleCloseDialogCloseSuggestedJobs,
    handleSuggestedJobs,
    handleCreateSuggestedJobs,
    handleCloseWithConfirmation,
  } = useSuggestedJobs()

  const {
    isOpen: isDialogOpenJob,
    handleOpen: handleOpenDialogJob,
    handleClose: handleCloseDialogJob,
  } = useDialog()

  const { mutate: createJob } = useMutationPostCreateJobs({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs")
        showSnackbar({
          title: t("jobs.succeedCreateJob"),
          variant: "success",
        })
        setRichEditorResponsibilities("")
        setRichEditorQualifications("")
        handleCloseDialogJob()
      },
      onError: (error) => {
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        })
      },
    },
  })

  const { data: classData } = useQueryClassesById({
    id: Number(params.classId),
    options: {
      enabled: !!params.classId,
    },
  })

  return (
    <Box
      width="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      textAlign={
        betweenTwoSides ? "center" : { tablet: "start", mobile: "center" }
      }
      py={{ tablet: "62px", mobile: "16px" }}
    >
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.mockup.primary95,
            borderRadius: "50%",
            minWidth: "156px",
            height: "156px",
            mr: 3,
          }}
        >
          <Anonymous />
        </Box>
      )}
      <Box maxWidth="400px">
        <Box>
          <Typography
            sx={{ mb: 1 }}
            color="mockup.darkBlue"
            variant="subtitle1"
          >
            {t("jobs.youDoNotHaveAnyJobsYet")}
          </Typography>
          <Typography
            color="mockup.neutral20"
            variant="body2"
            mb={3}
            fontWeight="500"
          >
            {t("jobs.instructionJobs")}
          </Typography>
        </Box>
        <Box
          display="flex"
          sx={{
            justifyContent: { tablet: "start", mobile: "center" },
            flexDirection: betweenTwoSides
              ? { tablet: "column", mobile: "row" }
              : { tablet: "row", mobile: "column" },
            width: "100%",
            gap: 2,
          }}
        >
          <Button onClick={onAddJob ? onAddJob : handleOpenDialogJob}>
            {t("jobs.addJob")}
          </Button>
          <Button
            variant="outlined"
            onClick={
              onAddSuggestedJobs
                ? onAddSuggestedJobs
                : handleOpenDialogSuggested
            }
          >
            {t("jobs.addSuggestedJobs")}
          </Button>
          <Dialog
            titleText={t("jobs.addSuggestedJobs")}
            open={isDialogOpenSuggested}
            onClose={(event, reason) => {
              if (reason === "backdropClick") {
                handleOpenDialogCloseSuggestedJobs()
              }
              mixpanel.track("add_class_settings", {
                action: "cancel_add_suggested_job",
              })
            }}
            desktopMaxWidth="1040px"
            tabletMaxWidth="867px"
            actionAcceptText={t("jobs.addSelectedJobs", {
              count: checkedSuggestedJobs.length,
            })}
            onActionButtonClick={handleCreateSuggestedJobs}
          >
            <>
              <Typography variant="body2" color="mockup.neutral10">
                {t("jobs.clickOnTheTextToEditTheSuggestedSettings")}
              </Typography>
              <AddSuggestedJobs
                paymentClass={classData?.data}
                handleSuggestedJobs={handleSuggestedJobs}
                setChecked={setCheckedSuggestedJobs}
                checked={checkedSuggestedJobs}
                errorSuggested={errorSuggested}
                handleOpenDialogCloseSuggestedJobs={
                  handleOpenDialogCloseSuggestedJobs
                }
              />
            </>
          </Dialog>
          <Dialog
            titleText={t("jobs.addJob")}
            open={isDialogOpenJob}
            onClose={() => {
              setRichEditorResponsibilities("")
              setRichEditorQualifications("")
              handleCloseDialogJob()
              mixpanel.track("add_class_settings", {
                action: "cancel_add_job",
              })
            }}
            desktopMaxWidth="1040px"
            tabletMaxWidth="534px"
            actionAcceptText={t("jobs.addJob")}
            actionAcceptButtonProps={{ type: "submit", form: "addNewJob" }}
          >
            <AddJob
              richEditorQualifications={richEditorQualifications}
              setRichEditorQualifications={setRichEditorQualifications}
              setRichEditorResponsibilities={setRichEditorResponsibilities}
              richEditorResponsibilities={richEditorResponsibilities}
              id="addNewJob"
              createJob={createJob}
            />
          </Dialog>
          <Dialog
            open={isDialogCloseSuggestedJobs}
            onClose={handleCloseDialogCloseSuggestedJobs}
            titleText={t("jobs.areYouSureYouWantToExit")}
            descriptionText={t("jobs.noChangesWillBeSaved")}
            actionAcceptText={t("jobs.accept")}
            onActionButtonClick={handleCloseWithConfirmation}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default NoJobs
